import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Link2,
  MailPlus,
  PenSquare,
  Activity,
  Trash2,
  Megaphone,
  SlidersHorizontal,
  FileText,
  Wallet,
  LineChart,
  CircleCheck,
  Circle,
  Sparkles,
  Loader2,
  MoreVertical,
  TrendingUp,
  Share2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AIInput } from '@/components/ui/ai-input';
import { AITextarea } from '@/components/ui/ai-textarea';
import { AIRichTextEditor } from '@/components/ui/ai-rich-text-editor';
import { richHtmlToPayload, sanitizeRichHtml, stripHtmlToText } from '@/lib/html-utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type {
  ApiResponse,
  AutomationRun,
  AutomationWorkflow,
  Campaign,
  CampaignChannel,
  CampaignContent,
  CampaignType,
  ContentStatus,
  IntegrationConfig,
  MarketingActivity,
  MarketingPhase,
  MarketingPlan,
  MarketingStrategy,
  Supplier,
  CampaignAiGenerateActivitiesResult,
} from '@/types';
import { InfoTooltip } from '@/components/help/InfoTooltip';
import { api } from '@/lib/api';
import { socialApi, type SocialAccount, type SocialPost } from '@/lib/social-api';
import { cn, formatCurrency, statusColors } from '@/lib/utils';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { CreateActivityDialog } from '@/components/activities/CreateActivityDialog';
import { CampaignPublishPanel } from '@/components/campaigns/CampaignPublishPanel';
import { withReturnTo } from '@/lib/return-path';

const CAMPAIGN_TYPE_LABELS: Record<CampaignType, string> = {
  B2B: 'B2B',
  B2C: 'B2C',
  mixed: 'Gemischt',
};

const CHANNEL_LABELS: Record<CampaignChannel, string> = {
  email: 'E-Mail',
  google: 'Google',
  linkedin: 'LinkedIn',
  print: 'Print',
  social: 'Social',
  multi: 'Multi',
  other: 'Sonstige',
};

const CONTENT_STATUS_LABELS: Record<ContentStatus, string> = {
  draft: 'Entwurf',
  review: 'Review',
  approved: 'Genehmigt',
  published: 'Veröffentlicht',
  rejected: 'Abgelehnt',
};

const CONTENT_STATUS_FLOW: ContentStatus[] = ['draft', 'review', 'approved', 'published'];

/** Aligns helper rows in multi-column grids (same min height ≈ two lines of text-xs). */
const FIELD_HINT_CLASS =
  'min-h-[2.75rem] text-xs leading-snug text-muted';

interface CampaignDetailPageProps {
  campaignId: string;
  onNavigate: (path: string) => void;
  onBack?: () => void;
}

export function CampaignDetailPage({ campaignId, onNavigate, onBack }: CampaignDetailPageProps) {
  const confirmDialog = useConfirmDialog();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [activeTab, setActiveTab] = useState('campaign');
  const [campaignActivities, setCampaignActivities] = useState<MarketingActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [addingContent, setAddingContent] = useState(false);
  const [integrationsLoading, setIntegrationsLoading] = useState(false);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [automationWorkflows, setAutomationWorkflows] = useState<AutomationWorkflow[]>([]);
  const [automationRuns, setAutomationRuns] = useState<AutomationRun[]>([]);
  const [scheduleWorkflowId, setScheduleWorkflowId] = useState<string>('');
  const [scheduleAt, setScheduleAt] = useState<string>('');
  const [integrations, setIntegrations] = useState<IntegrationConfig[]>([]);
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [socialPosts, setSocialPosts] = useState<SocialPost[]>([]);
  const [socialLoading, setSocialLoading] = useState(false);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [newContentText, setNewContentText] = useState('');
  const [createActivityDialogOpen, setCreateActivityDialogOpen] = useState(false);
  const [aiActivitiesDialogOpen, setAiActivitiesDialogOpen] = useState(false);
  const [aiActivitiesMode, setAiActivitiesMode] = useState<'mixed' | 'research'>('mixed');
  const [aiResearchRunId, setAiResearchRunId] = useState('');
  const [aiActivitiesCount, setAiActivitiesCount] = useState(8);
  const [aiActivitiesHint, setAiActivitiesHint] = useState('');
  const [aiActivitiesLoading, setAiActivitiesLoading] = useState(false);
  const [aiActivitiesError, setAiActivitiesError] = useState<string | null>(null);
  const [deactivatingActivityId, setDeactivatingActivityId] = useState<string | null>(null);
  const [researchRunsLoading, setResearchRunsLoading] = useState(false);
  const [researchRunsForCampaign, setResearchRunsForCampaign] = useState<MarketingStrategy['research_runs']>([]);
  const [error, setError] = useState<string | null>(null);
  const [saveWarning, setSaveWarning] = useState<string | null>(null);
  /** Phasen des verknüpften Plans — für Einblendung der UTM-Sektion */
  const [planPhases, setPlanPhases] = useState<MarketingPhase[]>([]);
  const [googleAdsCampaignIdDraft, setGoogleAdsCampaignIdDraft] = useState('');
  const [linkedinAdsCampaignIdDraft, setLinkedinAdsCampaignIdDraft] = useState('');
  const [adLinkSaving, setAdLinkSaving] = useState<'google_ads' | 'linkedin_ads' | null>(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    status: 'draft' as ContentStatus,
    type: 'B2C' as CampaignType,
    channel: 'multi' as CampaignChannel,
    supplier_id: null as string | null,
    budget_total: 0,
    budget_supplier: 0,
    budget_own: 0,
    expected_leads: 0,
    actual_leads: 0,
    expected_revenue: 0,
    actual_revenue: 0,
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    landing_url: '',
  });

  const hydrateForm = (nextCampaign: Campaign) => {
    setForm({
      name: nextCampaign.name ?? '',
      description: sanitizeRichHtml(nextCampaign.description ?? nextCampaign.description_md ?? ''),
      status: nextCampaign.status,
      type: nextCampaign.type,
      channel: nextCampaign.channel,
      supplier_id: nextCampaign.supplier_id ?? null,
      budget_total: nextCampaign.budget_total ?? 0,
      budget_supplier: nextCampaign.budget_supplier ?? 0,
      budget_own: nextCampaign.budget_own ?? 0,
      expected_leads: nextCampaign.expected_leads ?? 0,
      actual_leads: nextCampaign.actual_leads ?? 0,
      expected_revenue: nextCampaign.expected_revenue ?? 0,
      actual_revenue: nextCampaign.actual_revenue ?? 0,
      utm_source: nextCampaign.utm_source ?? '',
      utm_medium: nextCampaign.utm_medium ?? '',
      utm_campaign: nextCampaign.utm_campaign ?? '',
      landing_url: nextCampaign.landing_url ?? '',
    });
  };

  const campaignLoadRequest = useRef(0);
  const activitiesLoadRequest = useRef(0);
  const loadCampaign = useCallback(async () => {
    const request = ++campaignLoadRequest.current;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<ApiResponse<Campaign & { content_items?: CampaignContent[] }>>(
        `/campaigns/${campaignId}`
      );
      if (request !== campaignLoadRequest.current) return;
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Kampagnen-Details konnten nicht geladen werden.');
      }
      const hydratedCampaign: Campaign = {
        ...res.data,
        content: res.data.content_items ?? [],
      };
      setCampaign(hydratedCampaign);
      hydrateForm(hydratedCampaign);

      if (hydratedCampaign.plan_id) {
        try {
          const planRes = await api.get<ApiResponse<MarketingPlan>>(`/marketing-plans/${hydratedCampaign.plan_id}`);
          if (request !== campaignLoadRequest.current) return;
          setPlanPhases(Array.isArray(planRes.data?.phases) ? planRes.data.phases : []);
        } catch {
          if (request === campaignLoadRequest.current) setPlanPhases([]);
        }
      } else {
        setPlanPhases([]);
      }
    } catch (nextError) {
      if (request !== campaignLoadRequest.current) return;
      setCampaign(null);
      setPlanPhases([]);
      setError(nextError instanceof Error ? nextError.message : 'Kampagnen-Details konnten nicht geladen werden.');
    } finally {
      if (request === campaignLoadRequest.current) setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    void loadCampaign();
    return () => { campaignLoadRequest.current += 1; activitiesLoadRequest.current += 1; };
  }, [loadCampaign]);

  const loadCampaignActivities = useCallback(async () => {
    const request = ++activitiesLoadRequest.current;
    setActivitiesLoading(true);
    try {
      const res = await api.get<ApiResponse<MarketingActivity[]>>(`/campaigns/${campaignId}/activities`);
      if (request !== activitiesLoadRequest.current) return;
      setCampaignActivities(Array.isArray(res.data) ? res.data : []);
    } catch {
      if (request === activitiesLoadRequest.current) setCampaignActivities([]);
    } finally {
      if (request === activitiesLoadRequest.current) setActivitiesLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    if (activeTab !== 'activities') return;
    void loadCampaignActivities();
  }, [activeTab, campaignId, loadCampaignActivities]);

  const handleDeactivateCampaignActivity = async (act: MarketingActivity) => {
    const confirmed = await confirmDialog({
      title: 'Aktivität deaktivieren?',
      description: `«${act.title}» wird aus der Übersicht entfernt — unabhängig vom Status (z. B. Backlog oder To-Do). Sie erscheint nicht mehr in Plan- und Aktivitätslisten.`,
      confirmText: 'Deaktivieren',
      cancelText: 'Abbrechen',
      variant: 'destructive',
    });
    if (!confirmed) return;
    setDeactivatingActivityId(act.id);
    try {
      const res = await api.patch<ApiResponse<{ message?: string }>>(`/activities/${act.id}/deactivate`);
      if (!res.success) {
        throw new Error(res.error ?? 'Aktivität konnte nicht deaktiviert werden.');
      }
      toast.success('Aktivität deaktiviert.');
      await loadCampaignActivities();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Aktivität konnte nicht deaktiviert werden.');
    } finally {
      setDeactivatingActivityId(null);
    }
  };

  useEffect(() => {
    if (!aiActivitiesDialogOpen || !campaign?.plan_id) return;
    let cancelled = false;
    setResearchRunsLoading(true);
    setResearchRunsForCampaign([]);
    void (async () => {
      try {
        const planRes = await api.get<ApiResponse<MarketingPlan & { phases?: unknown[] }>>(
          `/marketing-plans/${campaign.plan_id}`
        );
        const sid = planRes.data?.strategy_id;
        if (!sid) {
          if (!cancelled) setResearchRunsLoading(false);
          return;
        }
        const stratRes = await api.get<ApiResponse<MarketingStrategy>>(`/marketing-strategies/${sid}`);
        if (!cancelled) setResearchRunsForCampaign(stratRes.data?.research_runs ?? []);
      } catch {
        if (!cancelled) setResearchRunsForCampaign([]);
      } finally {
        if (!cancelled) setResearchRunsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aiActivitiesDialogOpen, campaign?.plan_id]);

  useEffect(() => {
    if (aiActivitiesMode !== 'research' || !researchRunsForCampaign?.length) return;
    const firstRun = researchRunsForCampaign[0];
    if (!firstRun) return;
    const stillValid = researchRunsForCampaign.some((r) => r.id === aiResearchRunId);
    if (!aiResearchRunId || !stillValid) {
      setAiResearchRunId(firstRun.id);
    }
  }, [aiActivitiesMode, researchRunsForCampaign, aiResearchRunId]);

  useEffect(() => {
    const loadIntegrations = async () => {
      setIntegrationsLoading(true);
      try {
        const res = await api.get<ApiResponse<IntegrationConfig[]>>('/integrations?limit=100');
        setIntegrations(Array.isArray(res.data) ? res.data : []);
      } catch {
        setIntegrations([]);
      } finally {
        setIntegrationsLoading(false);
      }
    };
    void loadIntegrations();
  }, []);

  useEffect(() => {
    const loadSuppliers = async () => {
      try {
        const res = await api.get<ApiResponse<Supplier[]>>('/suppliers?limit=200');
        setSuppliers(Array.isArray(res.data) ? res.data : []);
      } catch {
        setSuppliers([]);
      }
    };
    void loadSuppliers();
  }, []);

  useEffect(() => {
    const links = campaign?.external_campaign_links ?? [];
    setGoogleAdsCampaignIdDraft(links.find((l) => l.platform === 'google_ads')?.external_campaign_id ?? '');
    setLinkedinAdsCampaignIdDraft(links.find((l) => l.platform === 'linkedin_ads')?.external_campaign_id ?? '');
  }, [campaign?.id, campaign?.external_campaign_links]);

  useEffect(() => {
    const loadAutomation = async () => {
      if (!campaign) return;
      setAutomationLoading(true);
      try {
        const [statusRes, workflowRes] = await Promise.all([
          api.get<ApiResponse<{ recent_runs?: AutomationRun[] }>>(`/campaigns/${campaign.id}/automation-status`),
          api.get<ApiResponse<AutomationWorkflow[]>>('/automation/workflows'),
        ]);
        setAutomationRuns(Array.isArray(statusRes.data?.recent_runs) ? statusRes.data.recent_runs : []);
        const workflows = Array.isArray(workflowRes.data) ? workflowRes.data : [];
        setAutomationWorkflows(workflows);
        if (!scheduleWorkflowId && workflows.length > 0 && workflows[0]) {
          setScheduleWorkflowId(workflows[0].id);
        }
      } catch {
        setAutomationRuns([]);
        setAutomationWorkflows([]);
      } finally {
        setAutomationLoading(false);
      }
    };
    void loadAutomation();
  }, [campaign, scheduleWorkflowId]);

  useEffect(() => {
    if (activeTab !== 'publish' || !campaign) return;
    let cancelled = false;
    const loadSocial = async () => {
      setSocialLoading(true);
      try {
        const [accounts, posts] = await Promise.all([
          socialApi.listAccounts(),
          socialApi.listPosts({ campaign_id: campaign.id, limit: 20 }),
        ]);
        if (cancelled) return;
        setSocialAccounts(Array.isArray(accounts) ? accounts : []);
        setSocialPosts(Array.isArray(posts) ? posts : []);
      } catch {
        if (!cancelled) {
          setSocialAccounts([]);
          setSocialPosts([]);
        }
      } finally {
        if (!cancelled) setSocialLoading(false);
      }
    };
    void loadSocial();
    return () => {
      cancelled = true;
    };
  }, [activeTab, campaign]);

  const handleUpdateCampaign = async () => {
    if (!campaign) return;
    if (!form.name.trim()) {
      setError('Bitte einen Kampagnennamen eingeben.');
      return;
    }
    setSaving(true);
    setError(null);
    setSaveWarning(null);
    try {
      const payload = {
        name: form.name.trim(),
        description: richHtmlToPayload(form.description),
        description_md: richHtmlToPayload(form.description),
        status: form.status,
        type: form.type,
        channel: form.channel,
        supplier_id: form.supplier_id || null,
        budget_total: Math.max(0, Number(form.budget_total) || 0),
        budget_supplier: Math.max(0, Number(form.budget_supplier) || 0),
        budget_own: Math.max(0, Number(form.budget_own) || 0),
        expected_leads: Math.max(0, Number(form.expected_leads) || 0),
        actual_leads: Math.max(0, Number(form.actual_leads) || 0),
        expected_revenue: Math.max(0, Number(form.expected_revenue) || 0),
        actual_revenue: Math.max(0, Number(form.actual_revenue) || 0),
        utm_source: form.utm_source?.trim() || null,
        utm_medium: form.utm_medium?.trim() || null,
        utm_campaign: form.utm_campaign?.trim() || null,
        landing_url: form.landing_url?.trim() || null,
      };
      const res = await api.patch<ApiResponse<Campaign>>(`/campaigns/${campaign.id}`, payload);
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Kampagne konnte nicht gespeichert werden.');
      }
      if (res.warning) {
        toast.warning('Kampagne gespeichert mit Hinweis', { description: res.warning });
        setSaveWarning(res.warning);
      } else {
        toast.success('Kampagne gespeichert.');
        setSaveWarning(null);
      }
      const updatedCampaign = { ...campaign, ...res.data };
      setCampaign(updatedCampaign);
      hydrateForm(updatedCampaign);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Kampagne konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCampaign = async () => {
    if (!campaign) return;
    const confirmed = await confirmDialog({
      title: 'Kampagne deaktivieren?',
      description:
        `«${campaign.name}» wird aus der Übersicht entfernt — auch im Status Entwurf. Alle mit dieser Kampagne verknüpften Aktivitäten werden ebenfalls deaktiviert und erscheinen nicht mehr in den Plan-Ansichten.`,
      confirmText: 'Deaktivieren',
      cancelText: 'Abbrechen',
      variant: 'destructive',
    });
    if (!confirmed) return;
    setDeleting(true);
    setError(null);
    try {
      const res = await api.patch<ApiResponse<{ activities_deactivated?: number }>>(
        `/campaigns/${campaign.id}/deactivate`
      );
      if (!res.success) {
        throw new Error(res.error ?? 'Kampagne konnte nicht deaktiviert werden.');
      }
      const n = res.data?.activities_deactivated;
      if (typeof n === 'number' && n > 0) {
        toast.success('Kampagne deaktiviert.', {
          description: `${n} verknüpfte Aktivität${n === 1 ? '' : 'en'} ebenfalls entfernt.`,
        });
      } else {
        toast.success('Kampagne deaktiviert.');
      }
      onNavigate('/campaigns');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Kampagne konnte nicht deaktiviert werden.');
    } finally {
      setDeleting(false);
    }
  };

  const handleAiGenerateCampaignActivities = async () => {
    setAiActivitiesError(null);
    if (aiActivitiesMode === 'research' && !aiResearchRunId) {
      setAiActivitiesError('Bitte einen Research-Lauf wählen.');
      return;
    }
    setAiActivitiesLoading(true);
    try {
      const res = await api.post<ApiResponse<CampaignAiGenerateActivitiesResult>>(
        `/campaigns/${campaignId}/ai-generate-activities`,
        {
          mode: aiActivitiesMode,
          research_run_id: aiActivitiesMode === 'research' ? aiResearchRunId : undefined,
          count: Math.min(15, Math.max(4, aiActivitiesCount)),
          context_hint: aiActivitiesHint.trim() || undefined,
        },
        { timeoutMs: 120000 }
      );
      if (!res.success || !res.data) {
        throw new Error(res.error ?? 'Aktivitäten konnten nicht generiert werden.');
      }
      setAiActivitiesDialogOpen(false);
      setActiveTab('activities');
      await loadCampaignActivities();
    } catch (e) {
      setAiActivitiesError(e instanceof Error ? e.message : 'Aktivitäten konnten nicht generiert werden.');
    } finally {
      setAiActivitiesLoading(false);
    }
  };

  const handleAdvanceContentStatus = async (content: CampaignContent, status: ContentStatus) => {
    if (!campaign || content.status === status) return;
    try {
      await api.patch<ApiResponse<CampaignContent>>(`/campaigns/${campaign.id}/content/${content.id}`, { status });
      await loadCampaign();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Content-Status konnte nicht geändert werden.');
    }
  };

  const handleAddContent = async () => {
    if (!campaign) return;
    const trimmed = newContentText.trim();
    if (!trimmed) return;
    setAddingContent(true);
    setError(null);
    try {
      await api.post<ApiResponse<CampaignContent>>(`/campaigns/${campaign.id}/content`, {
        title: trimmed.length > 80 ? `${trimmed.slice(0, 77)}...` : trimmed,
        content: trimmed,
        content_md: trimmed,
        status: 'draft',
      });
      setNewContentText('');
      await loadCampaign();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Content konnte nicht hinzugefügt werden.');
    } finally {
      setAddingContent(false);
    }
  };

  const createTemplate = (template: 'newsletter' | 'social') => {
    if (!campaign) return;
    const text =
      template === 'newsletter'
        ? `Newsletter-Entwurf: ${campaign.name}

Zielgruppe:
Kernaussage:
Call-to-Action:
Angebot/Mehrwert:
Tracking-Hinweis:`
        : `Social-Post-Entwurf: ${campaign.name}

Hook:
Hauptbotschaft:
Call-to-Action:
Visual-Idee:
Hashtags:`;
    setNewContentText(text);
    setActiveTab('content');
  };

  const openActivityCreate = (type: 'email' | 'social' | 'campaign') => {
    window.localStorage.setItem('leftclick_open_create_activity', '1');
    onNavigate(`/activities?type=${type}`);
  };

  const integrationByType = (type: IntegrationConfig['type']) =>
    integrations.find((item) => item.type === type && item.status === 'active');

  const saveAdPlatformLink = async (
    platform: 'google_ads' | 'linkedin_ads',
    opts?: { clear?: boolean }
  ) => {
    if (!campaign) return;
    setAdLinkSaving(platform);
    try {
      const value =
        opts?.clear === true
          ? ''
          : platform === 'google_ads'
            ? googleAdsCampaignIdDraft.trim()
            : linkedinAdsCampaignIdDraft.trim();
      const res = await api.put<ApiResponse<{ external_campaign_id?: string | null }>>(
        `/campaigns/${campaign.id}/ad-platform-link`,
        {
          platform,
          external_campaign_id: value.length > 0 ? value : null,
        }
      );
      if (!res.success) {
        throw new Error(res.error ?? 'Verknüpfung konnte nicht gespeichert werden.');
      }
      if (opts?.clear) {
        if (platform === 'google_ads') setGoogleAdsCampaignIdDraft('');
        else setLinkedinAdsCampaignIdDraft('');
      }
      toast.success(
        opts?.clear
          ? 'Verknüpfung entfernt.'
          : platform === 'google_ads'
            ? 'Google-Ads-Verknüpfung gespeichert.'
            : 'LinkedIn-Ads-Verknüpfung gespeichert.'
      );
      await loadCampaign();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Verknüpfung konnte nicht gespeichert werden.');
    } finally {
      setAdLinkSaving(null);
    }
  };

  const handlePublishAll = async (dryRun = false) => {
    if (!campaign) return;
    try {
      await api.post<ApiResponse<Record<string, unknown>>>(`/campaigns/${campaign.id}/publish-all`, {
        dry_run: dryRun,
      });
      const statusRes = await api.get<ApiResponse<{ recent_runs?: AutomationRun[] }>>(`/campaigns/${campaign.id}/automation-status`);
      setAutomationRuns(Array.isArray(statusRes.data?.recent_runs) ? statusRes.data.recent_runs : []);
      setActiveTab('automation');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Publish-All konnte nicht gestartet werden.');
    }
  };

  const handleSchedule = async () => {
    if (!campaign || !scheduleWorkflowId || !scheduleAt) {
      setError('Bitte Workflow und Zeitpunkt auswählen.');
      return;
    }
    try {
      await api.post<ApiResponse<Record<string, unknown>>>(`/campaigns/${campaign.id}/schedule`, {
        workflow_id: scheduleWorkflowId,
        publish_at: new Date(scheduleAt).toISOString(),
      });
      const statusRes = await api.get<ApiResponse<{ recent_runs?: AutomationRun[] }>>(`/campaigns/${campaign.id}/automation-status`);
      setAutomationRuns(Array.isArray(statusRes.data?.recent_runs) ? statusRes.data.recent_runs : []);
      setActiveTab('automation');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Planung konnte nicht gespeichert werden.');
    }
  };

  /** UTM-Bereich: einblenden sobald eine Planphase in Umsetzung ist; bei bereits gespeicherten Werten immer; ohne Phasen im Plan weiterhin sichtbar (Abwärtskompatibilität). */
  const trackingSectionVisible = useMemo(() => {
    const hasUtmData =
      Boolean(
        (form.utm_source?.trim() ?? '') ||
          (form.utm_medium?.trim() ?? '') ||
          (form.utm_campaign?.trim() ?? '') ||
          (form.landing_url?.trim() ?? '')
      );
    if (hasUtmData) return true;
    if (planPhases.length === 0) return true;
    return planPhases.some((p) => p.status === 'in_progress' || p.status === 'completed');
  }, [planPhases, form.utm_source, form.utm_medium, form.utm_campaign, form.landing_url]);

  const checklist = useMemo(() => {
    const nameOk = form.name.trim().length >= 2;
    const d = stripHtmlToText(form.description).trim();
    const descOk = d.length === 0 || d.length >= 20;
    const budgetSum = Number(form.budget_own) + Number(form.budget_supplier);
    const total = Number(form.budget_total);
    const budgetMismatch =
      total > 0 && budgetSum > 0 && Math.abs(total - budgetSum) > 0.01;
    return { nameOk, descOk, budgetSum, budgetMismatch };
  }, [form]);

  return (
    <div className="min-h-full w-full space-y-6 px-4 py-6 sm:px-6 lg:mx-auto lg:max-w-6xl lg:px-8">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" onClick={() => (onBack ? onBack() : onNavigate('/campaigns'))}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Zurück
          </Button>
        </div>
        <div className="min-w-0 flex-1 text-right sm:text-left">
          <p className="text-xs font-semibold uppercase tracking-widest text-foreground dark:text-muted">
            Marketing-Kampagne
          </p>
          <h1 className="mt-1 text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
            Kampagnen-Details
          </h1>
        </div>
      </div>

      {error && (
        <div
          className="rounded-xl border border-danger/30 bg-danger-soft px-4 py-3 text-sm text-danger"
          role="alert"
        >
          {error}
        </div>
      )}

      {saveWarning && (
        <div
          className="rounded-xl border border-warning/30 bg-warning-soft px-4 py-3 text-sm text-warning"
          role="status"
        >
          {saveWarning}
        </div>
      )}

      {loading ? (
        <Card className="border-border/80 dark:border-border">
          <CardContent className="py-16 text-center text-sm text-muted">Details werden geladen…</CardContent>
        </Card>
      ) : !campaign ? (
        <Card className="border-border/80 dark:border-border">
          <CardContent className="py-16 text-center text-sm text-muted">Kampagne konnte nicht geladen werden.</CardContent>
        </Card>
      ) : (
        <>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 rounded-xl bg-card-hover/90 p-1.5 dark:bg-card/70">
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <TabsTrigger value="campaign" className="gap-1.5 data-[state=active]:shadow-sm">
                    <Megaphone className="h-4 w-4" />
                    Kampagne
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">Name, Status, Typ, Kanal, Budget, Zulieferer — Basis der Kampagne.</TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <TabsTrigger value="activities" className="gap-1.5 data-[state=active]:shadow-sm">
                    <Activity className="h-4 w-4" />
                    Aktivitäten
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">Mit diesem Plan verknüpfte Marketing-Aktivitäten.</TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <TabsTrigger value="content" className="data-[state=active]:shadow-sm">
                    Content
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">E-Mails, Posts, Landing-Texte — inkl. Status und Freigaben.</TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <TabsTrigger value="integrations" className="data-[state=active]:shadow-sm">
                    Integrationen
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">Angebundene Dienste (z. B. Mailchimp) für diese Kampagne.</TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <TabsTrigger value="publish" className="gap-1.5 data-[state=active]:shadow-sm">
                    <Share2 className="h-4 w-4" />
                    Veröffentlichen
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">Social-Kanäle, Composer und letzte Posts zu dieser Kampagne.</TooltipContent>
              </Tooltip>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  <TabsTrigger value="automation" className="data-[state=active]:shadow-sm">
                    Automatisierung
                  </TabsTrigger>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs text-sm">Workflows, Publishing, Zeitplan — Status der Runs einsehen.</TooltipContent>
              </Tooltip>
            </TabsList>

            <TabsContent value="campaign" className="mt-0 space-y-6 focus-visible:outline-none">
              <div className="relative overflow-hidden rounded-2xl border border-border/90 bg-gradient-to-br from-primary/15 via-background to-card-hover/80 p-6 shadow-sm dark:border-border/80 dark:from-primary-hover/40 dark:via-background/80 dark:to-background sm:p-8">
                <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full bg-muted/20 blur-3xl dark:bg-muted/10" />
                <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={cn('border-0', statusColors[form.status])}>{CONTENT_STATUS_LABELS[form.status]}</Badge>
                      <Badge variant="secondary">{CAMPAIGN_TYPE_LABELS[form.type]}</Badge>
                      <Badge variant="outline">{CHANNEL_LABELS[form.channel]}</Badge>
                    </div>
                    <h2 className="mt-3 text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                      {campaign.name}
                    </h2>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted">
                      Gehen Sie die vier Schritte durch: <strong className="font-semibold text-foreground">Name</strong> →{' '}
                      <strong className="font-semibold text-foreground">Rahmen</strong> (Status, Typ, Kanal, Lieferant) →{' '}
                      <strong className="font-semibold text-foreground">Beschreibung</strong> →{' '}
                      <strong className="font-semibold text-foreground">Budget & Kennzahlen</strong>. Anschließend speichern.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {[
                  { ok: checklist.nameOk, label: '1 · Name', hint: 'Klarer Titel für alle Beteiligten' },
                  {
                    ok: checklist.descOk,
                    label: '2 · Beschreibung',
                    hint: checklist.descOk
                      ? 'Optional – Ziel & Zielgruppe beschreiben'
                      : 'Mind. 20 Zeichen oder Feld leeren',
                  },
                  { ok: true, label: '3 · Rahmen', hint: 'Status, Typ, Kanal, Zulieferer' },
                  {
                    ok: !checklist.budgetMismatch,
                    label: '4 · Budget',
                    hint: checklist.budgetMismatch ? 'Gesamt passt nicht zu Eigen + Lieferant' : 'CHF und Aufteilung',
                  },
                ].map((item) => (
                  <div
                    key={item.label}
                    className={cn(
                      'flex gap-3 rounded-xl border p-3 transition-colors',
                      item.ok
                        ? 'border-success/30 bg-success-soft'
                        : 'border-warning/30 bg-warning-soft'
                    )}
                  >
                    {item.ok ? (
                      <CircleCheck className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                    ) : (
                      <Circle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                    )}
                    <div className="min-w-0">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted">{item.label}</p>
                      <p className="mt-0.5 text-xs text-muted">{item.hint}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-6">
                <section className="rounded-2xl border border-border/90 bg-card/80 p-5 shadow-sm dark:bg-background/40 sm:p-6">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-sm">
                      1
                    </span>
                    <div>
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                        <Megaphone className="h-5 w-5 text-foreground" />
                        Name & Kürzel
                      </h3>
                      <p className="mt-1 text-sm text-muted">
                        So erscheint die Kampagne in Listen und Reports. Kurz und eindeutig wählen.
                      </p>
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="campaign-name">Name</Label>
                    <AIInput
                      id="campaign-name"
                      value={form.name}
                      onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                      onValueChange={(v) => setForm((prev) => ({ ...prev, name: v }))}
                      placeholder="z. B. Swisscom Q1 Lead Engine"
                      aiFieldLabel="Kampagnenname"
                      aiContext={`Kampagne Typ: ${form.type}, Kanal: ${form.channel}`}
                      className="text-base"
                    />
                  </div>
                </section>

                <section className="rounded-2xl border border-border/90 bg-card/80 p-5 shadow-sm dark:bg-background/40 sm:p-6">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-sm">
                      2
                    </span>
                    <div>
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                        <SlidersHorizontal className="h-5 w-5 text-foreground" />
                        Rahmen & Zulieferer
                      </h3>
                      <p className="mt-1 text-sm text-muted">
                        Status steuert, ob die Kampagne aktiv geschaltet werden darf. Typ und Kanal helfen bei der Auswertung.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="grid gap-2">
                      <Label>Status</Label>
                      <Select
                        value={form.status}
                        onValueChange={(value) => setForm((prev) => ({ ...prev, status: value as ContentStatus }))}
                      >
                        <SelectTrigger className="bg-card dark:bg-background">
                          <SelectValue placeholder="Status" />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(CONTENT_STATUS_LABELS) as ContentStatus[]).map((key) => (
                            <SelectItem key={key} value={key}>
                              {CONTENT_STATUS_LABELS[key]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className={FIELD_HINT_CLASS}>Entwurf bis zur Freigabe, dann Live schalten.</p>
                    </div>
                    <div className="grid gap-2">
                      <Label>Typ</Label>
                      <Select
                        value={form.type}
                        onValueChange={(value) => setForm((prev) => ({ ...prev, type: value as CampaignType }))}
                      >
                        <SelectTrigger className="bg-card dark:bg-background">
                          <SelectValue placeholder="Typ" />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(CAMPAIGN_TYPE_LABELS) as CampaignType[]).map((key) => (
                            <SelectItem key={key} value={key}>
                              {CAMPAIGN_TYPE_LABELS[key]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className={FIELD_HINT_CLASS} aria-hidden="true">
                        &nbsp;
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label>Kanal</Label>
                      <Select
                        value={form.channel}
                        onValueChange={(value) => setForm((prev) => ({ ...prev, channel: value as CampaignChannel }))}
                      >
                        <SelectTrigger className="bg-card dark:bg-background">
                          <SelectValue placeholder="Kanal" />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(CHANNEL_LABELS) as CampaignChannel[]).map((key) => (
                            <SelectItem key={key} value={key}>
                              {CHANNEL_LABELS[key]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className={FIELD_HINT_CLASS} aria-hidden="true">
                        &nbsp;
                      </p>
                    </div>
                    <div className="grid gap-2">
                      <Label>Zulieferer</Label>
                      <Select
                        value={form.supplier_id ?? 'none'}
                        onValueChange={(value) =>
                          setForm((prev) => ({ ...prev, supplier_id: value === 'none' ? null : value }))
                        }
                      >
                        <SelectTrigger className="bg-card dark:bg-background">
                          <SelectValue placeholder="Kein Zulieferer" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Kein Zulieferer</SelectItem>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className={FIELD_HINT_CLASS}>Optional – für Co-Funding und Reporting.</p>
                    </div>
                  </div>
                </section>

                <section className="rounded-2xl border border-border/90 bg-card/80 p-5 shadow-sm dark:bg-background/40 sm:p-6">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-sm">
                      3
                    </span>
                    <div>
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                        <FileText className="h-5 w-5 text-foreground" />
                        Beschreibung
                      </h3>
                      <p className="mt-1 text-sm text-muted">
                        Ziel, Zielgruppe, Botschaft und Zeitplan – hilft Teams und KI-Assistenten im gleichen Kontext.
                      </p>
                    </div>
                  </div>
                  <div className="relative">
                    <AIRichTextEditor
                      value={form.description}
                      onValueChange={(v) => setForm((prev) => ({ ...prev, description: v }))}
                      placeholder="Kurz: Was soll erreicht werden? Wer ist die Zielgruppe? Welche Kanäle/Meilensteine?"
                      aiFieldLabel="Kampagnenbeschreibung"
                      aiContext={`Kampagne: ${form.name}, Typ: ${form.type}, Kanal: ${form.channel}`}
                    />
                    <p className="mt-2 flex items-center gap-1.5 text-xs text-muted">
                      <Sparkles className="h-3.5 w-3.5 text-muted" />
                      KI-Icons: Textvorschläge für Name und Beschreibung – Inhalt bleibt in Ihrer Verantwortung.
                    </p>
                  </div>
                </section>

                <section className="rounded-2xl border border-border/90 bg-card/80 p-5 shadow-sm dark:bg-background/40 sm:p-6">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary text-sm font-bold text-primary-foreground shadow-sm">
                      4
                    </span>
                    <div>
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                        <Wallet className="h-5 w-5 text-foreground" />
                        Budget (CHF)
                      </h3>
                      <p className="mt-1 text-sm text-muted">
                        Eigenbudget und Anteil des Lieferanten erfassen. Gesamt sollte in der Regel der Summe der beiden Teile
                        entsprechen.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <div className="grid gap-2">
                      <Label>Eigen</Label>
                      <Input
                        type="number"
                        min={0}
                        className="font-mono tabular-nums"
                        value={form.budget_own}
                        onChange={(event) => setForm((prev) => ({ ...prev, budget_own: Number(event.target.value) }))}
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Lieferant</Label>
                      <Input
                        type="number"
                        min={0}
                        className="font-mono tabular-nums"
                        value={form.budget_supplier}
                        onChange={(event) =>
                          setForm((prev) => ({ ...prev, budget_supplier: Number(event.target.value) }))
                        }
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label>Gesamt</Label>
                      <Input
                        type="number"
                        min={0}
                        className="font-mono tabular-nums"
                        value={form.budget_total}
                        onChange={(event) => setForm((prev) => ({ ...prev, budget_total: Number(event.target.value) }))}
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-background px-4 py-3 text-sm dark:bg-card/60">
                    <span className="text-muted">
                      Summe Eigen + Lieferant:{' '}
                      <strong className="font-semibold text-foreground">
                        {formatCurrency(checklist.budgetSum)}
                      </strong>
                    </span>
                    {checklist.budgetMismatch && (
                      <span className="text-warning">Hinweis: Gesamt weicht von der Summe ab.</span>
                    )}
                  </div>
                </section>

                {trackingSectionVisible ? (
                  <section className="rounded-2xl border border-border/90 bg-card/80 p-5 shadow-sm dark:bg-background/40 sm:p-6">
                    <div className="mb-4 flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-card-hover text-sm font-bold text-muted dark:text-foreground">
                        <Link2 className="h-4 w-4" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="text-lg font-semibold text-foreground">
                            Tracking &amp; Landingpage
                          </h3>
                          <InfoTooltip
                            side="right"
                            text="Diese Felder steuern nur die Dokumentation von UTM-Parametern für Webanalytics (z. B. Google Analytics, Matomo): gleiche Werte in Anzeigen-Links und E-Mails verwenden, damit Besuche der Kampagne zugeordnet werden können. Für Spend und Conversions aus Google Ads oder LinkedIn ist das nicht nötig — dafür unter «Integrationen» die Kampagnen-ID verknüpfen und Ad-Daten sync auslösen."
                          />
                        </div>
                        <p className="mt-1 text-sm leading-relaxed text-muted">
                          <strong className="font-medium text-muted">Musst du nicht ausfüllen</strong>, wenn du
                          nur Google- oder LinkedIn-Spend in dieser App messen willst: dafür reicht die Verknüpfung unter{' '}
                          <span className="font-medium text-muted">Integrationen</span> mit der
                          Kampagnen-ID. UTM hier ist nur für die{' '}
                          <span className="font-medium text-muted">Website-/Analytics-Zuordnung</span> (welche
                          Links ihr mit welchen Parametern schaltet) — sinnvoll, sobald ihr Analytics oder Landingpages konsistent
                          taggen wollt.
                        </p>
                        {saveWarning && (
                          <div
                            className="mt-3 rounded-lg border border-warning/30 bg-warning-soft px-3 py-2 text-sm text-warning"
                            role="status"
                          >
                            <strong className="font-semibold">Hinweis zum Speichern:</strong> {saveWarning}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="grid gap-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="utm_source" className="inline">
                            Quelle
                          </Label>
                          <span className="font-mono text-xs text-muted">utm_source</span>
                          <InfoTooltip text="Woher kommt der Traffic: z. B. google, newsletter, linkedin. Entspricht oft dem Plattform- oder Quellennamen in deinen Reports." />
                        </div>
                        <Input
                          id="utm_source"
                          value={form.utm_source}
                          onChange={(e) => setForm((p) => ({ ...p, utm_source: e.target.value }))}
                          placeholder="z. B. google, newsletter"
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="grid gap-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="utm_medium" className="inline">
                            Medium
                          </Label>
                          <span className="font-mono text-xs text-muted">utm_medium</span>
                          <InfoTooltip text="Art des Kanals: z. B. cpc (Bezahlte Klicks), email, social, banner. Hilft, bezahlte vs. organische Zugriffe zu unterscheiden." />
                        </div>
                        <Input
                          id="utm_medium"
                          value={form.utm_medium}
                          onChange={(e) => setForm((p) => ({ ...p, utm_medium: e.target.value }))}
                          placeholder="z. B. cpc, email"
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="grid gap-2 sm:col-span-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="utm_campaign" className="inline">
                            Kampagnenname
                          </Label>
                          <span className="font-mono text-xs text-muted">utm_campaign</span>
                          <InfoTooltip text="Freier Name für diese Kampagne; am besten konsistent mit dem Kampagnennamen hier in der App. Wird in Analytics als Kampagnen-Label geführt." />
                        </div>
                        <Input
                          id="utm_campaign"
                          value={form.utm_campaign}
                          onChange={(e) => setForm((p) => ({ ...p, utm_campaign: e.target.value }))}
                          placeholder="Kampagnenname oder interner Code"
                          className="font-mono text-sm"
                        />
                      </div>
                      <div className="grid gap-2 sm:col-span-2">
                        <div className="flex items-center gap-1.5">
                          <Label htmlFor="landing_url" className="inline">
                            Landing-URL
                          </Label>
                          <span className="font-mono text-xs text-muted">https</span>
                          <InfoTooltip text="Die Zielseite nach dem Klick (mit https). In Anzeigen und E-Mails sollten dieselben UTM-Parameter an die URL angehängt werden wie hier dokumentiert." />
                        </div>
                        <Input
                          id="landing_url"
                          value={form.landing_url}
                          onChange={(e) => setForm((p) => ({ ...p, landing_url: e.target.value }))}
                          placeholder="https://…"
                          className="font-mono text-sm"
                          inputMode="url"
                          autoComplete="url"
                        />
                      </div>
                    </div>
                  </section>
                ) : (
                  <section className="rounded-2xl border border-dashed border-border/90 bg-background/80 p-5 dark:border-border dark:bg-card/40 sm:p-6">
                    <div className="flex items-start gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-card-hover/80 text-muted dark:bg-card-hover">
                        <Link2 className="h-4 w-4" />
                      </span>
                      <div className="min-w-0">
                        <h3 className="text-base font-semibold text-foreground">Tracking &amp; Landingpage</h3>
                        <p className="mt-1 text-sm text-muted">
                          Diese Felder werden eingeblendet, sobald mindestens eine Phase des Marketingplans auf «In Arbeit» oder
                          «Abgeschlossen» steht — typisch wenn Umsetzung oder Budgetierung geklärt sind (Ads, Newsletter, Links mit
                          Messung).
                        </p>
                        <p className="mt-2 text-xs leading-relaxed text-muted">
                          Ad-Spend und Plattform-Kennzahlen kommen über die Integrationen (Kampagnen-ID), nicht über diese UTM-Felder.
                          UTM-Parameter sind erst relevant, wenn ihr konkrete Ziel-URLs und Kanäle in Analytics auswerten wollt.
                        </p>
                      </div>
                    </div>
                  </section>
                )}

                {campaign.supplier && (
                  <div className="rounded-2xl border border-border/90 bg-background/90 p-4 dark:border-border dark:bg-card/50">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted">Verknüpfter Zulieferer</p>
                    <p className="mt-1 font-medium text-foreground">{campaign.supplier.name}</p>
                    {campaign.supplier.company && campaign.supplier.company !== campaign.supplier.name && (
                      <p className="text-sm text-muted">{campaign.supplier.company}</p>
                    )}
                    {campaign.supplier.contact_name && (
                      <p className="text-sm text-muted">{campaign.supplier.contact_name}</p>
                    )}
                    {campaign.supplier.contact_email && (
                      <p className="text-sm text-muted">{campaign.supplier.contact_email}</p>
                    )}
                  </div>
                )}

                <section className="rounded-2xl border border-border/90 bg-card/80 p-5 shadow-sm dark:bg-background/40 sm:p-6">
                  <div className="mb-4 flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-card-hover text-sm font-bold text-muted dark:text-foreground">
                      5
                    </span>
                    <div>
                      <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                        <LineChart className="h-5 w-5 text-foreground" />
                        Performance & Kennzahlen
                      </h3>
                      <p className="mt-1 text-sm text-muted">
                        Erwartete Leads und Umsatz – später mit Ist-Werten aus Kampagnen/CRM vergleichen.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div className="rounded-xl border border-border/80 p-4 dark:border-border">
                      <p className="text-sm font-medium text-muted">Leads</p>
                      <p className="mb-1 text-xs text-muted">erwartet · tatsächlich</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1">
                          <Label className="text-xs">Erwartet</Label>
                          <Input
                            type="number"
                            min={0}
                            className="font-mono tabular-nums"
                            value={form.expected_leads}
                            onChange={(event) =>
                              setForm((prev) => ({ ...prev, expected_leads: Number(event.target.value) }))
                            }
                          />
                        </div>
                        <div className="grid gap-1">
                          <Label className="text-xs">Tatsächlich</Label>
                          <Input
                            type="number"
                            min={0}
                            className="font-mono tabular-nums"
                            value={form.actual_leads}
                            onChange={(event) =>
                              setForm((prev) => ({ ...prev, actual_leads: Number(event.target.value) }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                    <div className="rounded-xl border border-border/80 p-4 dark:border-border">
                      <p className="text-sm font-medium text-muted">Umsatz (CHF)</p>
                      <p className="mb-1 text-xs text-muted">erwartet · tatsächlich</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="grid gap-1">
                          <Label className="text-xs">Erwartet</Label>
                          <Input
                            type="number"
                            min={0}
                            className="font-mono tabular-nums"
                            value={form.expected_revenue}
                            onChange={(event) =>
                              setForm((prev) => ({ ...prev, expected_revenue: Number(event.target.value) }))
                            }
                          />
                        </div>
                        <div className="grid gap-1">
                          <Label className="text-xs">Tatsächlich</Label>
                          <Input
                            type="number"
                            min={0}
                            className="font-mono tabular-nums"
                            value={form.actual_revenue}
                            onChange={(event) =>
                              setForm((prev) => ({ ...prev, actual_revenue: Number(event.target.value) }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 border-t border-border/80 bg-background/95 px-1 py-4 backdrop-blur-md dark:border-border dark:bg-background/90">
                  <Button
                    type="button"
                    variant="destructive"
                    disabled={deleting || saving}
                    onClick={() => void handleDeleteCampaign()}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    {deleting ? 'Wird deaktiviert…' : 'Deaktivieren'}
                  </Button>
                  <Button
                    size="lg"
                    className="min-w-[10rem] shadow-md"
                    disabled={saving || deleting}
                    onClick={handleUpdateCampaign}
                  >
                    {saving ? 'Speichern…' : 'Änderungen speichern'}
                  </Button>
                </div>
              </div>
            </TabsContent>

            <TabsContent value="activities" className="mt-0">
              <Card>
                <CardHeader>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Aktivitäten
                    </CardTitle>
                    <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="min-w-0 flex-1 sm:flex-initial"
                        onClick={() => {
                          setAiActivitiesError(null);
                          setAiActivitiesDialogOpen(true);
                        }}
                      >
                        <Sparkles className="mr-2 h-4 w-4 shrink-0" />
                        KI-Aktivitäten
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="min-w-0 flex-1 sm:flex-initial"
                        onClick={() => setCreateActivityDialogOpen(true)}
                      >
                        <PenSquare className="mr-2 h-4 w-4 shrink-0" />
                        Aktivität erstellen
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {activitiesLoading ? (
                    <p className="text-sm text-muted">Aktivitäten werden geladen…</p>
                  ) : campaignActivities.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-border p-6 text-center">
                      <Activity className="mx-auto mb-2 h-8 w-8 text-muted" />
                      <p className="text-sm text-muted">Noch keine Aktivitäten dieser Kampagne zugeordnet.</p>
                      <p className="mt-1 text-xs text-muted">
                        Nutze «KI-Aktivitäten» für einen Bulk-Vorschlag oder lege hier mit «Aktivität erstellen» Aufgaben
                        direkt für diese Kampagne an.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        className="mt-4"
                        onClick={() => setCreateActivityDialogOpen(true)}
                      >
                        <PenSquare className="mr-2 h-4 w-4" />
                        Aktivität erstellen
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {campaignActivities.map((act) => (
                        <div
                          key={act.id}
                          className="flex w-full items-center gap-1 rounded-lg border border-border px-3 py-2 transition-colors hover:bg-background dark:hover:bg-card/40 sm:gap-2 sm:px-4 sm:py-3"
                        >
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 flex-wrap items-center justify-between gap-2 text-left"
                            onClick={() =>
                              onNavigate(
                                withReturnTo(`/activities/${act.id}`, `/campaigns/${campaignId}`)
                              )
                            }
                          >
                            <div className="min-w-0 flex-1">
                              <p className="truncate font-medium">{act.title}</p>
                              <p className="text-xs text-muted">{act.type}</p>
                            </div>
                            <div className="flex shrink-0 flex-wrap items-center gap-2">
                              <Badge className={cn(statusColors[act.status])}>{act.status}</Badge>
                              <Badge variant="outline" className="text-xs">
                                {act.priority}
                              </Badge>
                            </div>
                          </button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 shrink-0 text-muted hover:text-foreground"
                                aria-label="Aktivitäten-Aktionen"
                                disabled={deactivatingActivityId === act.id}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {deactivatingActivityId === act.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <MoreVertical className="h-4 w-4" />
                                )}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuItem
                                onClick={() =>
                                  onNavigate(
                                    withReturnTo(`/activities/${act.id}`, `/campaigns/${campaignId}`)
                                  )
                                }
                              >
                                Öffnen
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-danger focus:text-danger"
                                onClick={() => void handleDeactivateCampaignActivity(act)}
                              >
                                Deaktivieren…
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="content" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Content</CardTitle>
                </CardHeader>
                <CardContent>
                  {campaign.content && campaign.content.length > 0 ? (
                    <ul className="space-y-3">
                      {campaign.content.map((item: CampaignContent) => (
                        <li key={item.id} className="rounded-lg border border-border p-3">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{item.title}</span>
                            <Badge className={cn(statusColors[item.status])}>{CONTENT_STATUS_LABELS[item.status]}</Badge>
                          </div>
                          {item.content && (
                            <p className="mt-2 line-clamp-2 text-sm text-muted">{item.content}</p>
                          )}
                          <div className="mt-2 flex flex-wrap gap-1">
                            {CONTENT_STATUS_FLOW.map((status) => (
                              <Button
                                key={status}
                                variant={item.status === status ? 'default' : 'outline'}
                                size="sm"
                                className="text-xs"
                                onClick={() => handleAdvanceContentStatus(item, status)}
                              >
                                {CONTENT_STATUS_LABELS[status]}
                              </Button>
                            ))}
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-sm text-muted">
                      Keine Content-Einträge. Content kann mit Text und Status-Workflow erstellt werden.
                    </p>
                  )}
                  <div className="mt-3">
                    <Label className="text-xs">Neuer Content</Label>
                    <AITextarea
                      className="mt-1"
                      rows={5}
                      placeholder="Inhalt eingeben…"
                      value={newContentText}
                      onChange={(event) => setNewContentText(event.target.value)}
                      onValueChange={setNewContentText}
                      aiFieldLabel="Kampagnen-Content"
                      aiContext={campaign ? `Kampagne: ${campaign.name}, Kanal: ${campaign.channel}` : 'Marketing-Kampagne'}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2"
                      disabled={addingContent || !newContentText.trim()}
                      onClick={handleAddContent}
                    >
                      {addingContent ? 'Wird hinzugefügt…' : 'Content hinzufügen'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="integrations" className="mt-0">
              <Card>
                <CardHeader>
                  <CardTitle>Integrationen & Aktionen</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="rounded-lg border border-border p-3">
                    <p className="text-sm text-muted">
                      Von hier kannst du direkt in Integrationen springen und aus der Kampagne Vorlagen für Newsletter oder Social Posts starten.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => onNavigate('/admin/integrations')}>
                        <Link2 className="mr-2 h-4 w-4" />
                        Integrationen öffnen
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => openActivityCreate('campaign')}>
                        <PenSquare className="mr-2 h-4 w-4" />
                        Aktivität aus Kampagne erstellen
                      </Button>
                    </div>
                  </div>

                  {(campaign.channel === 'google' || campaign.channel === 'multi' || campaign.channel === 'linkedin') && (
                    <div className="w-full rounded-lg border border-border p-4">
                      <div className="mb-3 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                        <div className="min-w-0">
                          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                            <LineChart className="h-4 w-4 shrink-0 text-foreground" />
                            Ad-Plattformen &amp; Messdaten
                          </h3>
                          <p className="mt-1 text-xs leading-relaxed text-muted">
                            Bestehende Kampagnen in Google Ads oder LinkedIn Campaign Manager verknüpfen — Spend und Conversions
                            fliessen in Statistik und Pacing ein. Die ID ist rein numerisch (aus der jeweiligen Oberfläche
                            kopieren).
                          </p>
                        </div>
                      </div>
                      <div className="grid w-full gap-4 sm:grid-cols-2">
                        {(campaign.channel === 'google' || campaign.channel === 'multi') && (
                          <div className="flex min-h-[11rem] flex-col rounded-lg border border-border/90 bg-background/50 p-3 dark:border-border dark:bg-card/30">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="h-4 w-4 text-accent" />
                              <p className="text-sm font-medium text-foreground">Google Ads</p>
                            </div>
                            <p className="mt-1 text-xs text-muted">
                              Status Integration:{' '}
                              {integrationsLoading
                                ? '…'
                                : integrationByType('google_ads')
                                  ? 'verbunden'
                                  : 'nicht verbunden'}
                            </p>
                            <Label htmlFor="ga-campaign-id" className="mt-2">
                              Kampagnen-ID
                            </Label>
                            <Input
                              id="ga-campaign-id"
                              className="mt-1 font-mono text-sm"
                              inputMode="numeric"
                              autoComplete="off"
                              placeholder="z. B. 12345678901"
                              value={googleAdsCampaignIdDraft}
                              onChange={(e) => setGoogleAdsCampaignIdDraft(e.target.value.replace(/\D/g, ''))}
                            />
                            {(() => {
                              const ga = campaign.external_campaign_links?.find((l) => l.platform === 'google_ads');
                              const ts = ga?.last_synced_at;
                              return ts ? (
                                <p className="mt-1 text-[11px] text-muted">
                                  Letzter Sync: {new Date(String(ts)).toLocaleString('de-CH')}
                                </p>
                              ) : null;
                            })()}
                            <div className="mt-auto flex flex-wrap gap-2 pt-3">
                              <Button
                                type="button"
                                size="sm"
                                disabled={adLinkSaving === 'google_ads'}
                                onClick={() => void saveAdPlatformLink('google_ads')}
                              >
                                {adLinkSaving === 'google_ads' ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Speichern…
                                  </>
                                ) : (
                                  'Speichern'
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={
                                  adLinkSaving === 'google_ads' ||
                                  (!googleAdsCampaignIdDraft.trim() &&
                                    !campaign.external_campaign_links?.some((l) => l.platform === 'google_ads'))
                                }
                                onClick={() => void saveAdPlatformLink('google_ads', { clear: true })}
                              >
                                Entfernen
                              </Button>
                            </div>
                          </div>
                        )}
                        {(campaign.channel === 'linkedin' || campaign.channel === 'multi') && (
                          <div className="flex min-h-[11rem] flex-col rounded-lg border border-border/90 bg-background/50 p-3 dark:border-border dark:bg-card/30">
                            <div className="flex items-center gap-2">
                              <Megaphone className="h-4 w-4 text-accent" />
                              <p className="text-sm font-medium text-foreground">LinkedIn Ads</p>
                            </div>
                            <p className="mt-1 text-xs text-muted">
                              Status Integration:{' '}
                              {integrationsLoading ? '…' : integrationByType('linkedin') ? 'verbunden' : 'nicht verbunden'}
                            </p>
                            <Label htmlFor="li-campaign-id" className="mt-2">
                              Kampagnen-ID
                            </Label>
                            <Input
                              id="li-campaign-id"
                              className="mt-1 font-mono text-sm"
                              inputMode="numeric"
                              autoComplete="off"
                              placeholder="Numerische Sponsored-Campaign-ID"
                              value={linkedinAdsCampaignIdDraft}
                              onChange={(e) => setLinkedinAdsCampaignIdDraft(e.target.value.replace(/\D/g, ''))}
                            />
                            {(() => {
                              const li = campaign.external_campaign_links?.find((l) => l.platform === 'linkedin_ads');
                              const ts = li?.last_synced_at;
                              return ts ? (
                                <p className="mt-1 text-[11px] text-muted">
                                  Letzter Sync: {new Date(String(ts)).toLocaleString('de-CH')}
                                </p>
                              ) : null;
                            })()}
                            <div className="mt-auto flex flex-wrap gap-2 pt-3">
                              <Button
                                type="button"
                                size="sm"
                                disabled={adLinkSaving === 'linkedin_ads'}
                                onClick={() => void saveAdPlatformLink('linkedin_ads')}
                              >
                                {adLinkSaving === 'linkedin_ads' ? (
                                  <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Speichern…
                                  </>
                                ) : (
                                  'Speichern'
                                )}
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={
                                  adLinkSaving === 'linkedin_ads' ||
                                  (!linkedinAdsCampaignIdDraft.trim() &&
                                    !campaign.external_campaign_links?.some((l) => l.platform === 'linkedin_ads'))
                                }
                                onClick={() => void saveAdPlatformLink('linkedin_ads', { clear: true })}
                              >
                                Entfernen
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                      <p className="mt-3 text-xs text-muted">
                        Nach dem Speichern unter{' '}
                        <button
                          type="button"
                          className="font-medium text-foreground underline-offset-2 hover:underline dark:text-muted"
                          onClick={() => onNavigate('/admin/integrations')}
                        >
                          Integrationen
                        </button>{' '}
                        «Ad-Daten sync» auslösen oder den geplanten Job abwarten; Auswertung unter Statistik.
                      </p>
                    </div>
                  )}

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg border border-border p-3">
                      <p className="text-sm font-medium">E-Mail / Newsletter</p>
                      <p className="text-xs text-muted">
                        Status: {integrationsLoading ? 'wird geladen…' : integrationByType('mailchimp') || integrationByType('lemlist') ? 'verbunden' : 'nicht verbunden'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => createTemplate('newsletter')}>
                          <MailPlus className="mr-2 h-4 w-4" />
                          Newsletter-Vorlage
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openActivityCreate('email')}>
                          E-Mail-Aktivität
                        </Button>
                      </div>
                    </div>

                    <div className="rounded-lg border border-border p-3">
                      <p className="text-sm font-medium">Social</p>
                      <p className="text-xs text-muted">
                        Status: {integrationsLoading ? 'wird geladen…' : integrationByType('linkedin') ? 'LinkedIn verbunden' : 'nicht verbunden'}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => createTemplate('social')}>
                          Social-Vorlage
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => openActivityCreate('social')}>
                          Social-Aktivität
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="publish" className="mt-0">
              <Card>
                <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3 space-y-0">
                  <CardTitle>Veröffentlichen</CardTitle>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={() => onNavigate(`/composer?campaign_id=${campaign.id}`)}
                    >
                      <PenSquare className="mr-2 h-4 w-4" />
                      Im Composer öffnen
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onNavigate('/automation')}>
                      Zur Automatisierung
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => onNavigate('/social')}>
                      Social Hub
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {socialLoading ? (
                    <p className="text-sm text-muted">Social-Daten werden geladen…</p>
                  ) : (
                    <>
                      <div>
                        <h4 className="mb-2 text-sm font-medium">Verbundene Kanäle</h4>
                        {socialAccounts.length === 0 ? (
                          <p className="text-sm text-muted">
                            Noch keine Social-Accounts verbunden.{' '}
                            <button
                              type="button"
                              className="font-medium text-foreground underline"
                              onClick={() => onNavigate('/social/accounts')}
                            >
                              Jetzt verbinden
                            </button>
                          </p>
                        ) : (
                          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                            {socialAccounts.map((account) => (
                              <div
                                key={account.id}
                                className="flex items-center justify-between rounded-lg border border-border px-3 py-2"
                              >
                                <div>
                                  <p className="text-sm font-medium">{account.display_name}</p>
                                  <p className="text-xs text-muted capitalize">{account.platform}</p>
                                </div>
                                <Badge variant={account.status === 'active' ? 'default' : 'secondary'}>
                                  {account.status}
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <h4 className="mb-2 text-sm font-medium">Letzte Social-Posts (diese Kampagne)</h4>
                        {socialPosts.length === 0 ? (
                          <p className="text-sm text-muted">Noch keine Social-Posts für diese Kampagne.</p>
                        ) : (
                          <div className="space-y-2">
                            {socialPosts.map((post) => (
                              <div
                                key={post.id}
                                className="rounded-lg border border-border p-3"
                              >
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                  <p className="text-sm font-medium">
                                    {post.title || post.body?.slice(0, 80) || 'Ohne Titel'}
                                  </p>
                                  <Badge variant="outline">{post.post_kind}</Badge>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  {(post.targets ?? []).map((t) => (
                                    <Badge key={t.id} variant="secondary" className="text-[10px]">
                                      {t.platform}: {t.status}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="automation" className="mt-0">
              <CampaignPublishPanel
                automationLoading={automationLoading}
                automationWorkflows={automationWorkflows}
                automationRuns={automationRuns}
                scheduleWorkflowId={scheduleWorkflowId}
                scheduleAt={scheduleAt}
                onScheduleWorkflowIdChange={setScheduleWorkflowId}
                onScheduleAtChange={setScheduleAt}
                onPublishAll={(dryRun) => void handlePublishAll(dryRun)}
                onSchedule={() => void handleSchedule()}
              />
            </TabsContent>
          </Tabs>

          <Dialog
            open={aiActivitiesDialogOpen}
            onOpenChange={(open) => {
              setAiActivitiesDialogOpen(open);
              if (!open) setAiActivitiesError(null);
            }}
          >
            <DialogContent className="max-h-[min(90vh,40rem)] w-full max-w-lg overflow-y-auto">
              <DialogHeader>
                <DialogTitle>KI-Aktivitäten für diese Kampagne</DialogTitle>
                <DialogDescription>
                  Es werden mehrere Aktivitäten auf einmal angelegt (Standard: Backlog). Wähle eine breite Kanalmischung
                  oder einen Research-Lauf der zur Strategie des Marketingplans gehört.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-2">
                {aiActivitiesError && (
                  <p
                    className="rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger"
                    role="alert"
                  >
                    {aiActivitiesError}
                  </p>
                )}
                <div className="grid gap-2">
                  <Label>Modus</Label>
                  <Select
                    value={aiActivitiesMode}
                    onValueChange={(v) => {
                      setAiActivitiesMode(v as 'mixed' | 'research');
                      if (v === 'mixed') setAiResearchRunId('');
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mixed">Querschnitt — Kanäle mixen</SelectItem>
                      <SelectItem value="research">Aus Research — Strategie-Research nutzen</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {aiActivitiesMode === 'research' && (
                  <div className="grid gap-2">
                    <Label>Research-Lauf</Label>
                    {researchRunsLoading ? (
                      <p className="flex items-center gap-2 text-sm text-muted">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Research-Läufe werden geladen…
                      </p>
                    ) : (researchRunsForCampaign?.length ?? 0) === 0 ? (
                      <p className="text-sm text-muted">
                        Keine Research-Läufe für die Strategie dieses Plans. Führe Research in der Marketingstrategie aus
                        oder wechsle zu «Querschnitt».
                      </p>
                    ) : (
                      <Select value={aiResearchRunId} onValueChange={setAiResearchRunId}>
                        <SelectTrigger>
                          <SelectValue placeholder="Research wählen" />
                        </SelectTrigger>
                        <SelectContent>
                          {(researchRunsForCampaign ?? []).map((run) => (
                            <SelectItem key={run.id} value={run.id}>
                              <span className="line-clamp-1">
                                {(run.query ?? '').slice(0, 72)}
                                {run.query && run.query.length > 72 ? '…' : ''} ({run.status})
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="ai-act-count">Anzahl (4–15)</Label>
                  <Input
                    id="ai-act-count"
                    type="number"
                    min={4}
                    max={15}
                    value={aiActivitiesCount}
                    onChange={(e) => setAiActivitiesCount(Number(e.target.value))}
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="ai-act-hint">Zusatz (optional)</Label>
                  <Textarea
                    id="ai-act-hint"
                    rows={3}
                    placeholder="z. B. Fokus auf Lead-Gen, DACH, Q2…"
                    value={aiActivitiesHint}
                    onChange={(e) => setAiActivitiesHint(e.target.value)}
                    className="resize-y"
                  />
                </div>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setAiActivitiesDialogOpen(false)}
                  disabled={aiActivitiesLoading}
                >
                  Abbrechen
                </Button>
                <Button
                  type="button"
                  onClick={() => void handleAiGenerateCampaignActivities()}
                  disabled={
                    aiActivitiesLoading ||
                    (aiActivitiesMode === 'research' &&
                      (!(researchRunsForCampaign?.length ?? 0) || !aiResearchRunId))
                  }
                >
                  {aiActivitiesLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Generiere…
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      Generieren und speichern
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <CreateActivityDialog
            open={createActivityDialogOpen}
            onOpenChange={setCreateActivityDialogOpen}
            onCreated={() => void loadCampaignActivities()}
            campaignContext={{
              id: campaign.id,
              planId: campaign.plan_id,
              campaignName: campaign.name,
            }}
          />
        </>
      )}
    </div>
  );
}
